// Realize: a mind map, reached from inside the Notes tab.
//
// The captain asked for it in these words (2026-08-19): "a mind mapper where
// someone can have a main topic in the middle and include what ever they want to
// brain storm", and left the rest of it as design work.
//
// TWO VIEWS, ONE SCREEN. The maps you have, and the one you are in. Not two
// entries in `shell/screens.ts`: that file names SCREENS, and which map is open
// is a row rather than a place - the same answer Month gives for which month is
// on. It is also why this is not a fifth tab, which AGENTS.md names as the
// likely future accident: it is a screen reached from Notes, exactly as Month
// and Reminders are screens reached from Today.
//
// THE FIRST SIXTY SECONDS ARE THE DESIGN. An account with no maps gets one
// field and one button, and typing a topic into it makes the map and opens it -
// so the first thing anybody does here is type what they are thinking about. A
// map with nothing on it puts the cursor in the composer with the centre already
// chosen, so the second thing they do is type a branch. Nothing has to be
// discovered before either of those works.
//
// THE COMPOSER IS THE WHOLE INTERACTION, and it is one field rather than a
// toolbar. Whatever box is chosen, the field adds under it; Enter adds and
// leaves the field ready, so a burst of branches is a burst of typing rather
// than a lap of the screen per branch. Rename borrows the same field, which is
// what keeps the number of controls down to what a stranger can take in.
//
// A MAP ARRANGES ITSELF, AND DRAGGING WAS ADDED TO THAT RATHER THAN INSTEAD OF
// IT (captain, 2026-08-19, after seeing and accepting the computed-only map).
// Where a branch sits is COMPUTED (`realize-model.ts`) until somebody moves it,
// and that half may not be dropped: a map that has to be dragged into shape is a
// map that cannot be made on a phone or with a keyboard, which is most of this
// product's brief. So a new map arranges itself, a burst of branches typed into
// the composer arranges itself, every drag has a keyboard path to the same write
// (Alt and an arrow), and `Tidy up` clears the lot back to computed with an
// Undo. Only a branch with a stored pair overrides the arithmetic.

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '../day/Icon.tsx';
import { whenLabel } from '../notes/note-model.ts';
import { useMode } from '../shell/theme.ts';
import type { DeletedBranch, MindMap } from '../api/client.ts';
import { MapCanvas } from './MapCanvas.tsx';
import type { FocusAsk } from './MapCanvas.tsx';
import { arrangementOf, CENTRE, layout } from './realize-model.ts';
import type { PositionWrite } from './realize-model.ts';
import type { RealizeComposer, RealizeState } from './useRealize.ts';

interface Props {
  state: RealizeState;
  /**
   * Out of Realize, back to Notes.
   *
   * There is deliberately NO account button in either bar here, which every TAB
   * in this client carries: a screen that is not a tab reaches Settings by going
   * back to the one that opened it, the way Month, Reminders and Settings itself
   * all do. So the right-hand side of these bars is the empty box that keeps the
   * centred title centred, and nothing else.
   */
  onBack: () => void;
}

export function RealizeScreen({ state, onBack }: Props) {
  if (state.openId !== null) return <OneMap state={state} />;
  return <MapList state={state} onBack={onBack} />;
}

// ------------------------------------------------------------- the list -----

/**
 * The row beside the one that is going: the next, or the one before it when the
 * one going was last. Null when it was the only row, which is the case each
 * caller answers for itself.
 */
function neighbourOf<T extends { id: string }>(rows: readonly T[], id: string): T | null {
  const at = rows.findIndex((one) => one.id === id);
  if (at === -1) return null;
  return rows[at + 1] ?? rows[at - 1] ?? null;
}

/**
 * WHERE THE CURSOR GOES WHEN A ROW ON THIS SCREEN IS REMOVED - one mechanism for
 * every group that has rows, rather than one each.
 *
 * The control holding it unmounts and the browser puts focus on `<body>` - the
 * top of the document - so somebody working with a keyboard is thrown out of the
 * list they were in, one press after an act they may want to undo.
 *
 * Every group answers it with the same ladder, and the ORDER is the decision:
 * the next row, else the one before it, else a last rung the caller names. The
 * last rung is never "the way to start again" - somebody who has just deleted
 * something may want it back, so the control under their cursor is the way back
 * wherever there is one. Each caller says which it chose.
 *
 * The spots are keyed by the caller, and they are attached and detached by the
 * ref callbacks - so the effect asks the DOM whether the control that HELD the
 * cursor has gone rather than guessing it from a payload. Honoured on the render
 * `busy` causes, it would take the cursor while the write was still in the air
 * and the row was still there to look at.
 *
 * A LAST RUNG MAY BE A THUNK rather than a spot, which is what lets a group hand
 * the cursor somewhere it does not own: the deleted-branch shelf disappears
 * entirely with its last row, and the place to go from there is a box on the map
 * - which `MapCanvas` focuses through its own one-shot ask. Held here, the
 * handover still fires, because the spots outlive the group that filled them.
 */
function useCursorHandover(): {
  spot: (key: string) => (element: HTMLElement | null) => void;
  owner: (key: string) => (element: HTMLElement | null) => void;
  /** A null target puts the ask down again, for a write that was refused. */
  handOver: (from: string, to: string | (() => void) | null) => void;
  focusSpot: (key: string) => boolean;
} {
  const spots = useRef(new Map<string, HTMLElement>());
  const owners = useRef(new Map<string, HTMLElement>());
  const focusedOwner = useRef<string | null>(null);
  const displaced = useRef(new Set<string>());
  const handover = useRef<{ from: string; to: string | (() => void) } | null>(null);
  useEffect(() => {
    const rememberOwner = (event: FocusEvent): void => {
      focusedOwner.current = null;
      for (const [key, owner] of owners.current) {
        if (owner.contains(event.target as Node)) {
          focusedOwner.current = key;
          break;
        }
      }
    };
    document.addEventListener('focusin', rememberOwner);
    return () => document.removeEventListener('focusin', rememberOwner);
  }, []);
  useEffect(() => {
    const ask = handover.current;
    if (ask === null || spots.current.has(ask.from)) return;
    handover.current = null;
    if (!displaced.current.delete(ask.from)) return;
    if (typeof ask.to === 'function') ask.to();
    else spots.current.get(ask.to)?.focus();
  });
  const focusSpot = useCallback((key: string): boolean => {
    const target = spots.current.get(key);
    if (target === undefined) return false;
    if (document.activeElement === document.body) target.focus();
    return true;
  }, []);

  return {
    spot: (key) => (element) => {
      if (element === null) spots.current.delete(key);
      else spots.current.set(key, element);
    },
    owner: (key) => (element) => {
      if (element === null) {
        const leaving = owners.current.get(key);
        if (focusedOwner.current === key || leaving?.contains(document.activeElement)) {
          displaced.current.add(key);
          focusedOwner.current = null;
        }
        owners.current.delete(key);
      } else {
        owners.current.set(key, element);
        displaced.current.delete(key);
        if (element.contains(document.activeElement)) focusedOwner.current = key;
      }
    },
    handOver: (from, to) => {
      if (to === null) displaced.current.delete(from);
      handover.current = to === null ? null : { from, to };
    },
    focusSpot,
  };
}

function MapList({ state, onBack }: Props) {
  const [starting, setStarting] = useState(false);
  const writesBlocked = state.retryProblem !== null || state.branchCreationPending !== null;
  const creationPending = state.mapCreationPending !== null;

  // WHICH map is asking, held here rather than inside the row it is asked on -
  // the same reasoning `NotesScreen` writes out at the same point: a row keyed by
  // its id is re-rendered rather than remounted, so a confirmation kept inside it
  // stays armed for the life of the screen, and the next press of a red button
  // somebody read as "start the delete" destroys a map.
  const [arming, setArming] = useState<string | null>(null);
  useEffect(() => {
    if (state.deletingMapIds.size === 0) setArming(null);
  }, [state.deletingMapIds, state.maps]);
  useEffect(() => {
    if (state.mapCreationPending !== null) state.updateStarterDraft(state.mapCreationPending);
  }, [state.mapCreationPending, state.updateStarterDraft]);

  const maps = state.maps;
  const topic = state.starterDraft;

  /**
   * NOTHING AT ALL, which is not the same as no LIVE maps - and the difference is
   * what stops the welcome being shown to somebody who has just deleted
   * something.
   *
   * The invitation below and the label on the field are the greeting for an
   * account that has never made a map. A recoverable delete leaves the map in the
   * Deleted group (`moveMap` in `useRealize.ts`), so this stays false and the
   * screen shows what is true: the map is deleted, and `Restore` is right there.
   * Both lists are empty only before the first map exists. One notion of "empty"
   * is read in both places so the invitation and field label cannot disagree.
   */
  const nothingYet = maps !== null && maps.length === 0 && state.deletedMaps.length === 0;

  const { spot, owner, handOver, focusSpot } = useCursorHandover();
  useEffect(() => {
    const retry = state.retryFocus;
    if (retry === null || retry.mapId === undefined) return;
    const key = state.deletedMaps.some((map) => map.id === retry.mapId)
      ? `gone-${retry.mapId}`
      : state.maps?.some((map) => map.id === retry.mapId)
        ? `live-${retry.mapId}`
        : null;
    if (key !== null && focusSpot(key)) state.consumeRetryFocus();
  }, [focusSpot, state]);

  const start = (): void => {
    const submitted = topic;
    const named = submitted.trim();
    if (named === '' || starting || maps === null) return;
    setStarting(true);
    // CLEARED ONLY ONCE IT LANDED. Clearing first put the toast "That map was
    // not started" over an empty field, with the topic somebody typed gone - the
    // opposite of what the composer inside a map deliberately does, which is to
    // leave the words there to try again.
    void state.startMap(named).finally(() => setStarting(false));
  };

  return (
    <>
      <div className="topbar">
        <button className="iconbtn" aria-label="Back" onClick={onBack} disabled={creationPending}>
          <Icon name="chevL" />
        </button>
        <h1>Realize</h1>
        {/* The heading is centred by the two controls either side of it, and
            there is only one - so an empty box of the same size stands where the
            other would be, exactly as Settings and Month balance theirs. */}
        <span className="iconbtn" aria-hidden="true" />
      </div>

      <div className="scroll">
        <div className="wrap">
          <form
            className="rzstart"
            onSubmit={(event) => {
              event.preventDefault();
              start();
            }}
          >
            <label className="fieldlabel" htmlFor="rz-new">
              {nothingYet ? 'What are you thinking about?' : 'A new map'}
            </label>
            <div className="rzstart-in">
              <input
                id="rz-new"
                ref={spot('new')}
                type="text"
                value={topic}
                placeholder="Weekend in Lisbon"
                onChange={(event) => state.updateStarterDraft(event.target.value)}
                disabled={starting || creationPending || writesBlocked}
              />
              <button
                className="btn primary"
                disabled={
                  maps === null || topic.trim() === '' || state.busy || starting || writesBlocked
                }
              >
                <Icon name="plus" />
                {creationPending ? 'Try again' : 'Start a map'}
              </button>
            </div>
            <p className="fieldhint">
              Whatever you type goes in the middle. Everything else branches off it.
            </p>
          </form>

          {maps === null && state.problem === null && (
            <p className="sheetwait" role="status">
              Loading…
            </p>
          )}

          {maps !== null && maps.length > 0 && (
            <div className="rzcards">
              {maps.map((map) => (
                <MapCard
                  key={map.id}
                  map={map}
                  today={state.today}
                  busy={state.busy}
                  locked={creationPending || writesBlocked}
                  armed={arming === `live-${map.id}`}
                  deleting={state.deletingMapIds.has(map.id)}
                  cardRef={spot(`live-${map.id}`)}
                  ownerRef={owner(`live-${map.id}`)}
                  onOpen={() => state.openMap(map.id)}
                  onArm={() => setArming(`live-${map.id}`)}
                  onDisarm={() => setArming(null)}
                  onDelete={() => {
                    // Last rung: the `Restore` this delete is about to put on
                    // screen. There is no sibling card on an account with one
                    // map, and the way back has to be the thing under the cursor
                    // rather than several presses past the field for starting
                    // another one.
                    const near = neighbourOf(maps, map.id);
                    handOver(
                      `live-${map.id}`,
                      near === null ? `gone-${map.id}` : `live-${near.id}`,
                    );
                    void state.removeMap(map).then((done) => {
                      // The card is still there after a refused write, so the ask
                      // would sit until something else overwrote it.
                      if (!done) handOver(`live-${map.id}`, null);
                    });
                  }}
                />
              ))}
            </div>
          )}

          {nothingYet && (
            <p className="finetext">
              A mind map is one idea in the middle and whatever comes off it. Nothing is filed,
              nothing is due, and nothing here turns up on your day.
            </p>
          )}

          {/* The read failed and whatever was last on screen is still there, so
              this says which of the two you are looking at rather than replacing
              a list that may still be right. */}
          {state.problem !== null &&
            (maps === null ? (
              <div className="sheetproblem" role="alert">
                <p>{state.problem}</p>
                <button className="btn" onClick={state.refresh} disabled={state.busy}>
                  Try again
                </button>
              </div>
            ) : (
              <p className="sheetproblem" role="alert">
                This list may be out of date. {state.problem}
              </p>
            ))}

          <PendingWrite state={state} />
          <PendingBranchCreation state={state} />

          {state.deletedMaps.length > 0 && (
            <div className="setgroup">
              <h2>Deleted</h2>
              <div className="setlist">
                {state.deletedMaps.map((map) => (
                  <DeletedMapRow
                    key={map.id}
                    map={map}
                    today={state.today}
                    busy={state.busy || creationPending || writesBlocked}
                    rowRef={spot(`gone-${map.id}`)}
                    ownerRef={owner(`gone-${map.id}`)}
                    onRestore={() => {
                      // Last rung: the card it has just become. The row leaves
                      // this group either way, so where there is no other row
                      // the cursor follows the map to where it went.
                      const near = neighbourOf(state.deletedMaps, map.id);
                      handOver(
                        `gone-${map.id}`,
                        near === null ? `live-${map.id}` : `gone-${near.id}`,
                      );
                      void state.restoreMap(map);
                    }}
                  />
                ))}
              </div>
              <p className="finetext">A deleted map stays here until you restore it.</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

/**
 * One map on the list: the card body opens it, and the control beside it removes
 * it.
 *
 * TWO CONTROLS, SIDE BY SIDE, RATHER THAN ONE CARD THAT IS A BUTTON. A button
 * inside a button is not valid HTML and the outer one swallows the press, so the
 * card is a plain box holding the two - which also keeps the keyboard order the
 * reading order: the map, then what can be done to it.
 *
 * Both are named for the MAP they act on. Several cards make several buttons
 * whose visible word is the same, and "Delete" alone says which button exists
 * rather than which map it removes.
 *
 * EVERY CONTROL THAT LEADS TO A WRITE GOES DOWN WHILE ONE IS IN FLIGHT, the same
 * `disabled={busy}` every action in `OneMap` carries. Confirming twice inside one
 * round trip sends the delete twice, and the second lands on a map the first one
 * removed - so the refusal replaces the success on the one notice this shell has,
 * and the app says the delete failed after doing it. Cancel goes down with them:
 * left up, it is a button offering to call off a write that has already gone.
 * The armed map's opener goes down with them too.
 */
function MapCard({
  map,
  today,
  busy,
  locked,
  armed,
  deleting,
  cardRef,
  ownerRef,
  onOpen,
  onArm,
  onDisarm,
  onDelete,
}: {
  map: MindMap;
  today: string | null;
  /** A write is in flight - this screen's, not necessarily this card's. */
  busy: boolean;
  locked: boolean;
  /** Whether THIS card is the one being asked about. Owned by the screen. */
  armed: boolean;
  /** The confirmed deletion request for this card has not settled. */
  deleting: boolean;
  /** The list keeps these so it can hand the cursor on when a row is removed. */
  cardRef: (element: HTMLElement | null) => void;
  ownerRef: (element: HTMLElement | null) => void;
  onOpen: () => void;
  onArm: () => void;
  onDisarm: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="rzcard" ref={ownerRef}>
      {/* No `aria-label`, deliberately: the card's own words are its name, so
          the branch count and when it last changed are announced with the topic
          rather than replaced by a verb. */}
      <button
        className="rzcard-open"
        ref={cardRef}
        onClick={onOpen}
        disabled={deleting || locked || (busy && armed)}
      >
        <span className="rzcard-t">{map.heading}</span>
        <span className="rzcard-m">
          {/* The SERVER'S count, so the card and the map agree. */}
          {map.branches === 1 ? '1 branch' : `${map.branches} branches`}
          {today !== null && ` - ${whenLabel(map.updatedDay, today)}`}
        </span>
      </button>
      {armed ? (
        <>
          <button
            className="btn"
            aria-label={`Keep ${map.heading}`}
            onClick={onDisarm}
            disabled={busy || locked}
          >
            Cancel
          </button>
          <button
            className="btn danger"
            aria-label={`Yes, delete ${map.heading}`}
            onClick={onDelete}
            disabled={busy || locked}
          >
            Yes, delete
          </button>
          <p className="delwarn" role="alert">
            &quot;{map.heading}&quot; and every branch on it go to Deleted, and you can put them
            back from there.
          </p>
        </>
      ) : (
        // Colour on hover only. A red glyph on every card would draw a list of
        // maps as a list of warnings, and this delete is the recoverable one.
        <button
          className="iconbtn rzcard-del"
          aria-label={`Delete ${map.heading}`}
          onClick={onArm}
          disabled={busy || locked}
        >
          <Icon name="trash" />
        </button>
      )}
    </div>
  );
}

/** A map that was deleted. `busy` as `MapCard`. */
function PendingWrite({ state }: { state: RealizeState }) {
  if (state.retryProblem === null) return null;
  return (
    <div className="sheetproblem" role="alert">
      <p>{state.retryProblem}</p>
      <button className="btn" onClick={state.retryPending} disabled={state.busy}>
        Try again
      </button>
    </div>
  );
}

function PendingBranchCreation({ state, onRetry }: { state: RealizeState; onRetry?: () => void }) {
  const pending = state.branchCreationPending;
  if (pending === null) return null;
  return (
    <div className="sheetproblem" role="alert">
      <p>&quot;{pending.label}&quot; may already have been added at its original place.</p>
      <button
        className="btn"
        onClick={onRetry ?? (() => void state.retryBranchCreation())}
        disabled={state.busy}
      >
        Finish checking &quot;{pending.label}&quot;
      </button>
    </div>
  );
}

function DeletedMapRow({
  map,
  today,
  busy,
  rowRef,
  ownerRef,
  onRestore,
}: {
  map: MindMap;
  today: string | null;
  busy: boolean;
  rowRef: (element: HTMLElement | null) => void;
  ownerRef: (element: HTMLElement | null) => void;
  onRestore: () => void;
}) {
  return (
    <div className="setrow notedel" ref={ownerRef}>
      <span className="st">
        <b>{map.heading}</b>
        <span>
          {today === null
            ? 'Deleted'
            : `Deleted - last changed ${whenLabel(map.updatedDay, today)}`}
        </span>
      </span>
      <button
        className="btn"
        ref={rowRef}
        aria-label={`Restore ${map.heading}`}
        onClick={onRestore}
        disabled={busy}
      >
        Restore
      </button>
    </div>
  );
}

// -------------------------------------------------------------- one map -----

interface ViewportMemory {
  mapId: string;
  width: number;
  height: number;
  centre: { x: number; y: number };
  clientWidth: number;
  clientHeight: number;
  scrollLeft: number;
  scrollTop: number;
}

interface CanvasActions {
  select: (id: string) => void;
  rename: () => void;
  delete: () => void;
  move: (id: string, at: PositionWrite) => void;
}

function OneMap({ state }: { state: RealizeState }) {
  const composer: RealizeComposer = state.composer ?? {
    mapId: state.openId ?? '',
    draft: '',
    mode: 'add',
    targetId: state.selected,
    targetHeading: '',
    renameBasis: null,
    revision: 0,
  };
  const field = useRef<HTMLInputElement>(null);
  const mapRetry = useRef<HTMLButtonElement>(null);
  const restoreMapControl = useRef<HTMLButtonElement>(null);
  const canvas = useRef<HTMLDivElement>(null);
  const painted = useMode();
  const focusRevision = useRef(0);
  const landingFocus = useRef(false);
  const [deleting, setDeleting] = useState(false);
  const [dragging, setDragging] = useState(false);
  const viewport = useRef<ViewportMemory | null>(null);
  const canvasActions = useRef<CanvasActions | null>(null);
  const selectOnCanvas = useCallback((id: string) => canvasActions.current?.select(id), []);
  const renameOnCanvas = useCallback(() => canvasActions.current?.rename(), []);
  const deleteOnCanvas = useCallback(() => canvasActions.current?.delete(), []);
  const moveOnCanvas = useCallback(
    (id: string, at: PositionWrite) => canvasActions.current?.move(id, at),
    [],
  );

  /** Where the cursor is asked to go once a delete has taken the box it was on. */
  const [focus, setFocus] = useState<FocusAsk | null>(null);

  /** The same handover the map list uses, for the rows on the deleted shelf. */
  const { spot, owner, handOver } = useCursorHandover();

  const view = state.open;
  const { selected } = state;

  const targetWasRepaired = composer.targetId !== selected && composer.draft.trim() === '';
  const effectiveTargetId = targetWasRepaired ? selected : composer.targetId;
  useEffect(() => {
    if (!targetWasRepaired) return;
    state.updateComposer(composer.mapId, (held) => {
      const current = held ?? composer;
      if (current.targetId === selected || current.draft.trim() !== '') return current;
      return {
        ...current,
        mode: 'add',
        targetId: selected,
        targetHeading: '',
        renameBasis: null,
        revision: current.revision + 1,
      };
    });
  }, [composer, selected, state.updateComposer, targetWasRepaired]);

  useLayoutEffect(() => {
    landingFocus.current = document.activeElement === document.body;
  }, []);

  useEffect(() => {
    const moved = (): void => {
      focusRevision.current += 1;
      landingFocus.current = false;
    };
    document.addEventListener('focusin', moved);
    return () => document.removeEventListener('focusin', moved);
  }, []);

  // ABOVE THE EARLY RETURN, because hooks may not be called conditionally and
  // there is a `return` for the map that has not arrived yet just below.
  //
  // MEMOISED ON WHAT IT IS COMPUTED FROM, and not on the render: every keystroke
  // in the composer is `OneMap` state, so an unmemoised call re-arranged the
  // whole map per character typed and handed React a fresh object for every box
  // and every path to reconcile. There is deliberately no cap on branches, so
  // that cost has no ceiling.
  const plan = useMemo(
    () =>
      view === null
        ? null
        : layout(
            { topic: view.map.topic, heading: view.map.heading },
            view.nodes,
            view.renderScale,
            view.renderOriginX,
            view.renderOriginY,
            view.renderSpan,
          ),
    [view],
  );

  useEffect(() => {
    const retry = state.retryFocus;
    if (retry === null) return;
    if (document.activeElement !== document.body) {
      state.consumeRetryFocus();
      return;
    }
    if (view === null || plan === null) {
      if (state.mapProblem === null || mapRetry.current === null) return;
      mapRetry.current.focus();
      state.consumeRetryFocus();
      return;
    }
    if (retry.destination === 'composer') field.current?.focus();
    else if (view.map.deleted) {
      if (!state.deletedMaps.some((map) => map.id === view.map.id)) return;
      restoreMapControl.current?.focus();
    } else {
      const target = plan.nodes.some((node) => node.id === state.selected)
        ? state.selected
        : CENTRE;
      setFocus({ id: target });
    }
    state.consumeRetryFocus();
  }, [
    plan,
    state.consumeRetryFocus,
    state.deletedMaps,
    state.mapProblem,
    state.retryFocus,
    state.selected,
    view,
  ]);

  useLayoutEffect(() => {
    if (!landingFocus.current || view === null || field.current === null) return;
    if (document.activeElement === document.body) field.current.focus();
    landingFocus.current = false;
  }, [view]);

  useLayoutEffect(() => {
    const pane = canvas.current;
    if (dragging || pane === null || view === null || plan === null) return;

    const clientWidth = pane.clientWidth;
    const clientHeight = pane.clientHeight;
    const offsetX = Math.max(0, (clientWidth - plan.width) / 2);
    const offsetY = Math.max(0, (clientHeight - plan.height) / 2);
    const before = viewport.current;
    const anchorX =
      before === null || before.mapId !== view.map.id
        ? clientWidth / 2
        : Math.max(0, (before.clientWidth - before.width) / 2) +
          before.centre.x -
          before.scrollLeft;
    const anchorY =
      before === null || before.mapId !== view.map.id
        ? clientHeight / 2
        : Math.max(0, (before.clientHeight - before.height) / 2) +
          before.centre.y -
          before.scrollTop;
    const maxLeft = Math.max(0, plan.width - clientWidth);
    const maxTop = Math.max(0, plan.height - clientHeight);
    pane.scrollLeft = Math.min(maxLeft, Math.max(0, offsetX + plan.centre.x - anchorX));
    pane.scrollTop = Math.min(maxTop, Math.max(0, offsetY + plan.centre.y - anchorY));
    viewport.current = {
      mapId: view.map.id,
      width: plan.width,
      height: plan.height,
      centre: plan.centre,
      clientWidth,
      clientHeight,
      scrollLeft: pane.scrollLeft,
      scrollTop: pane.scrollTop,
    };
  }, [dragging, plan, view]);

  if (view === null || plan === null) {
    return (
      <>
        <MapBar
          heading=""
          onBack={state.closeMap}
          disabled={state.branchCreationPending !== null}
        />
        <div className="scroll">
          <div className="wrap">
            {state.mapProblem === null ? (
              <p className="sheetwait" role="status">
                Loading…
              </p>
            ) : (
              <div className="daystate">
                <h3>That map could not be opened.</h3>
                <p>{state.mapProblem}</p>
                <button className="btn" ref={mapRetry} onClick={state.refresh}>
                  Try again
                </button>
              </div>
            )}
          </div>
        </div>
      </>
    );
  }

  const map = view!.map;
  const readOnly = map.deleted;
  const restorableMap = readOnly
    ? (state.deletedMaps.find((candidate) => candidate.id === map.id) ?? null)
    : null;
  const deletingMap = state.deletingMapIds.has(map.id);
  const deletingInMap =
    deletingMap || deleting || view.nodes.some((node) => state.deletingBranchIds.has(node.id));
  const here = plan!.nodes.find((node) => node.id === selected) ?? plan!.nodes[0]!;
  const onCentre = here.id === CENTRE;
  const targetMissing = effectiveTargetId !== selected;
  const targetHeading = targetMissing ? composer.targetHeading : here.heading;
  const effectiveMode = targetWasRepaired ? 'add' : composer.mode;
  const creationPending = state.branchCreationPending !== null;
  const writesBlocked = state.retryProblem !== null || creationPending;
  const updateComposer = (update: (held: RealizeComposer) => RealizeComposer): void => {
    state.updateComposer(map.id, (held) => update(held ?? composer));
  };

  const submit = (): void => {
    if (readOnly || deletingInMap || targetMissing || writesBlocked) return;
    const words = composer.draft.trim();
    if (words === '') return;
    const revision = composer.revision;
    if (effectiveMode === 'rename') {
      const basis = composer.renameBasis;
      if (basis === null) return;
      void state.rename(words, basis).then((accepted) => {
        if (accepted === null) return;
        state.updateComposer(map.id, (held) => {
          const current = held ?? composer;
          if (current.revision === revision) {
            return {
              ...current,
              mode: 'add',
              draft: '',
              renameBasis: null,
              revision: current.revision + 1,
            };
          }
          return current.mode === 'rename' && current.renameBasis === basis
            ? { ...current, renameBasis: accepted }
            : current;
        });
      });
      return;
    }
    const focusAtStart = focusRevision.current;
    void state.addBranch(words).then((added) => {
      if (added) {
        state.updateComposer(map.id, (held) => {
          const current = held ?? composer;
          return current.revision === revision
            ? { ...current, draft: '', revision: current.revision + 1 }
            : current;
        });
      }
      // The field keeps the cursor either way: a burst of branches is a burst of
      // typing, and a failed one leaves the words there to try again.
      if (focusRevision.current === focusAtStart) field.current?.focus();
    });
  };

  const retryPendingBranchCreation = (): void => {
    const pending = state.branchCreationPending;
    if (pending === null) return;
    const revision = composer.revision;
    const clearMatchingDraft =
      effectiveMode === 'add' &&
      effectiveTargetId === pending.targetId &&
      composer.draft.trim() === pending.label;
    void state.retryBranchCreation().then((saved) => {
      if (!saved || !clearMatchingDraft) return;
      state.updateComposer(map.id, (held) => {
        const current = held ?? composer;
        return current.revision === revision && current.targetId === pending.targetId
          ? { ...current, draft: '', revision: current.revision + 1 }
          : current;
      });
    });
  };

  const startRename = (): void => {
    if (readOnly || state.busy || deletingInMap || targetMissing || writesBlocked) return;
    if (
      composer.mode === 'rename' &&
      effectiveTargetId === here.id &&
      composer.renameBasis?.value === here.label
    ) {
      field.current?.focus();
      return;
    }
    if (composer.draft !== '' && !window.confirm('Discard the words in the branch field?')) return;
    updateComposer((current) => ({
      ...current,
      mode: 'rename',
      draft: here.label,
      targetId: here.id,
      targetHeading: here.heading,
      renameBasis: {
        mapId: map.id,
        targetId: here.id,
        value: here.label,
        mapWriteVersion: map.writeVersion,
      },
      revision: current.revision + 1,
    }));
    field.current?.focus();
  };

  const retarget = (id: string): void => {
    if (writesBlocked || deletingMap) return;
    const wasMissing = composer.targetId !== selected;
    const target = plan.nodes.find((node) => node.id === id);
    updateComposer((current) => {
      if (current.targetId !== id) {
        return {
          ...current,
          mode: 'add',
          draft: current.draft,
          targetId: id,
          targetHeading: target?.heading ?? '',
          renameBasis: null,
          revision: current.revision + 1,
        };
      }
      return wasMissing
        ? {
            ...current,
            mode: 'add',
            targetHeading: target?.heading ?? current.targetHeading,
            renameBasis: null,
            revision: current.revision + 1,
          }
        : current;
    });
    state.select(id);
  };

  /**
   * ONE handler for both ways of deleting a branch - the Delete key on a box and
   * the button below the map - because they are one act and had drifted into two
   * behaviours.
   *
   * GUARDED HERE, not in `MapCanvas`: the canvas draws a map and reports a key
   * press, and teaching it about write state would put another shape of the same
   * rule in another place. This is `disabled={state.busy}` said the only way a
   * key press can say it. Without it, holding Delete auto-repeats into a second
   * write on a branch the first one already stamped, which answers `removed: 0`
   * and replaces an accurate count with "deleted" on the one notice this shell
   * has - so a keyboard would be guarded less than a pointer on the same write.
   *
   * AND THE CURSOR FOLLOWS THE SELECTION, which is what the rest of this tree
   * already does. The box that had focus has just been unmounted, so without the
   * ask the browser drops focus on `<body>` and a keyboard user is at the top of
   * the document, one press after an action they may want to undo. It lands on
   * the parent - the box the composer has re-aimed at - so they carry on from
   * where the branch was. The way back from there is the toast's own Undo, and
   * after it expires the `N deleted branches` shelf under the map.
   */
  const deleteBranch = (): void => {
    if (onCentre || state.busy || deletingInMap || readOnly || writesBlocked) return;
    const focusAtStart = focusRevision.current;
    setDeleting(true);
    void state
      .removeBranch()
      .then((landed) => {
        if (landed !== null && focusRevision.current === focusAtStart) {
          setFocus({ id: landed });
        }
      })
      .finally(() => setDeleting(false));
  };

  /**
   * The same ladder for the shelf under the map when a row is restored.
   * The row drops the moment the write lands
   * (`forgetDeletedBranch` in `useRealize.ts`), so the control that had the
   * cursor unmounts under it.
   *
   * Last rung: THE BOX THE COMPOSER IS AIMED AT. When the last row goes the whole
   * disclosure goes with it, so there is nothing left in this group to land on -
   * and the map is where the person was working and where the bar says the next
   * thing they type will go. It is asked for through `MapCanvas`'s own one-shot
   * focus request, which is why the rung is a thunk rather than a spot.
   */
  const handOverShelf = (branch: DeletedBranch): void => {
    const near = neighbourOf(view!.deletedBranches, branch.id);
    handOver(
      `shelf-${branch.id}`,
      near !== null ? `shelf-${near.id}` : () => setFocus({ id: here.id }),
    );
  };

  canvasActions.current = {
    select: retarget,
    rename: startRename,
    delete: deleteBranch,
    move: (id, at) => void state.moveBranch(id, at),
  };

  return (
    <>
      <MapBar heading={map.heading} onBack={state.closeMap} disabled={creationPending} />

      <div
        className="rzcanvas"
        ref={canvas}
        onScroll={(event) => {
          const remembered = viewport.current;
          if (remembered === null) return;
          remembered.clientWidth = event.currentTarget.clientWidth;
          remembered.clientHeight = event.currentTarget.clientHeight;
          remembered.scrollLeft = event.currentTarget.scrollLeft;
          remembered.scrollTop = event.currentTarget.scrollTop;
        }}
      >
        <MapCanvas
          layout={plan!}
          selected={here.id}
          selectionNeedsConfirmation={targetMissing}
          onSelect={selectOnCanvas}
          onRename={renameOnCanvas}
          onDelete={deleteOnCanvas}
          onMove={moveOnCanvas}
          movementDisabled={deletingInMap || readOnly || writesBlocked}
          onDraggingChange={setDragging}
          focus={focus}
          mode={painted}
        />
      </div>

      {view!.deletedBranches.length > 0 && (
        <DeletedBranches
          branches={view!.deletedBranches}
          busy={state.busy || readOnly || writesBlocked}
          rowRef={(id) => spot(`shelf-${id}`)}
          ownerRef={(id) => owner(`shelf-${id}`)}
          onRestore={(branch) => {
            handOverShelf(branch);
            void state.restoreBranch(branch);
          }}
        />
      )}

      {state.mapProblem !== null && (
        <p className="sheetproblem rzproblem" role="alert">
          This map may be out of date. {state.mapProblem}
        </p>
      )}

      <PendingWrite state={state} />
      <PendingBranchCreation state={state} onRetry={retryPendingBranchCreation} />

      <div className="rzbar">
        <form
          className="rzbar-in"
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          <label className="rzbar-on" htmlFor="rz-compose">
            {targetMissing ? (
              <>
                <b>{targetHeading}</b> is no longer here. Choose a new target for this draft.
              </>
            ) : effectiveMode === 'rename' ? (
              <>
                Renaming <b>{here.heading}</b>
              </>
            ) : onCentre ? (
              <>
                Adding a branch to <b>{here.heading}</b>
              </>
            ) : (
              <>
                Adding under <b>{here.heading}</b>
              </>
            )}
          </label>
          <div className="rzbar-row">
            <input
              id="rz-compose"
              ref={field}
              type="text"
              value={composer.draft}
              placeholder={effectiveMode === 'rename' ? 'New words' : 'Something to branch off'}
              onChange={(event) => {
                const nextDraft = event.target.value;
                updateComposer((current) => ({
                  ...current,
                  draft: nextDraft,
                  revision: current.revision + 1,
                }));
              }}
              disabled={deletingInMap || readOnly || creationPending || state.retryProblem !== null}
              onKeyDown={(event) => {
                // The way out of a rename, in the key everybody already presses
                // to get out of things. Adding has nothing to escape from.
                if (event.key === 'Escape' && effectiveMode === 'rename') {
                  event.preventDefault();
                  if (
                    composer.draft !== here.label &&
                    !window.confirm('Discard the words in the branch field?')
                  )
                    return;
                  updateComposer((current) => ({
                    ...current,
                    mode: 'add',
                    draft: '',
                    renameBasis: null,
                    revision: current.revision + 1,
                  }));
                }
              }}
            />
            <button
              className="btn primary"
              disabled={
                composer.draft.trim() === '' ||
                state.busy ||
                readOnly ||
                deletingInMap ||
                targetMissing ||
                writesBlocked ||
                (effectiveMode === 'rename' && composer.renameBasis === null)
              }
            >
              {effectiveMode === 'rename' ? 'Save' : 'Add'}
            </button>
          </div>
        </form>

        <div className="rzbar-acts">
          {readOnly && (
            <button
              className="btn"
              ref={restoreMapControl}
              onClick={() => {
                if (restorableMap !== null) void state.restoreMap(restorableMap);
              }}
              disabled={state.busy || state.retryProblem !== null || restorableMap === null}
            >
              <Icon name="history" />
              Restore map
            </button>
          )}
          <button
            className="btn"
            onClick={startRename}
            disabled={state.busy || deletingInMap || readOnly || targetMissing || writesBlocked}
          >
            <Icon name="pencil" />
            {onCentre ? 'Rename topic' : 'Rename'}
          </button>
          {/* Not drawn on the centre AT ALL, rather than drawn and disabled: the
              topic is not a branch, and a delete control beside it would be a
              promise the schema cannot keep - there is no node in the middle of
              a map to remove (migration 0007). The way to remove a whole map is
              on the list this screen came from. */}
          {!onCentre && (
            <button
              className="btn danger"
              onClick={deleteBranch}
              disabled={state.busy || deletingInMap || readOnly || writesBlocked}
            >
              <Icon name="trash" />
              Delete
            </button>
          )}

          {/* THE WAY BACK FROM AN ARRANGEMENT. A completed Tidy remains here as
              an Undo until it is restored or superseded, including after this
              map is opened again. */}
          {view!.arrangementUndoId !== null && (
            <button
              className="btn"
              onClick={state.undoTidy}
              disabled={state.busy || deletingInMap || readOnly || writesBlocked}
            >
              <Icon name="history" />
              Undo tidy up
            </button>
          )}
          {view!.hasManualPositions && (
            <button
              className="btn"
              onClick={() => void state.tidyUp(arrangementOf(plan!))}
              disabled={state.busy || deletingInMap || readOnly || writesBlocked}
            >
              <Icon name="grid" />
              Tidy up
            </button>
          )}
        </div>

        {/* Said once, under the field, and only while the map is still empty:
            it is the one thing about this screen that is not obvious from
            looking at it, and it stops being worth saying the moment there is a
            branch to press. */}
        {view!.nodes.length === 0 ? (
          <p className="rzhint">
            Type a branch and press Add. Choose any branch to add under it - by tapping it, or with
            the arrow keys.
          </p>
        ) : (
          !onCentre && (
            // Said only while a BRANCH is chosen, which is the only time either
            // half of it can be acted on - and said out loud because a map that
            // can be dragged looks exactly like one that cannot. The keyboard
            // half is named beside the pointer half rather than left to be
            // found: it is the same write, and somebody who cannot drag has no
            // other way to learn it exists.
            <p className="rzhint">
              Drag a branch to move it, or hold Alt and press an arrow key. Add and Shift to go
              further.
            </p>
          )
        )}
      </div>
    </>
  );
}

/**
 * The bar over one map. Its Back goes to the LIST of maps, not out of Realize:
 * which map is open is screen state rather than a place (`useRealize.ts`), so
 * this is the control that unwinds it, and the list's own Back is what leaves.
 */
function MapBar({
  heading,
  onBack,
  disabled = false,
}: {
  heading: string;
  onBack: () => void;
  disabled?: boolean;
}) {
  return (
    <div className="topbar">
      <button
        className="iconbtn"
        aria-label="Back to your maps"
        onClick={onBack}
        disabled={disabled}
      >
        <Icon name="chevL" />
      </button>
      <h1>{heading}</h1>
      <span className="iconbtn" aria-hidden="true" />
    </div>
  );
}

/**
 * The branches that were deleted off this map.
 *
 * A `<details>` because it is exactly that: shut by default, because a deleted
 * branch is not what anybody came here for, and one press from the person who
 * did. Kept off the map rather than drawn faintly on it - a branch that is gone
 * must not be somewhere the eye reads as still there.
 *
 * Only the ROOTS of what was deleted are ever in here, so one row is one act of
 * deleting and `restores` says how much comes back with it
 * (`api/src/db/repositories/realize.ts`).
 *
 * `busy` guards every control here for the reason `MapCard` writes out.
 */
function DeletedBranches({
  branches,
  busy,
  rowRef,
  ownerRef,
  onRestore,
}: {
  branches: readonly DeletedBranch[];
  /** A write is in flight - this screen's, not necessarily this row's. */
  busy: boolean;
  /**
   * Each row's spot in the map's focus handover, which the SCREEN owns: this
   * group disappears with its last row, so a handover held in here would unmount
   * before it could be honoured. `Restore` is the spot because it is the way back.
   */
  rowRef: (id: string) => (element: HTMLElement | null) => void;
  ownerRef: (id: string) => (element: HTMLElement | null) => void;
  onRestore: (branch: DeletedBranch) => void;
}) {
  return (
    <details className="rzdel">
      <summary>
        {branches.length === 1 ? '1 deleted branch' : `${branches.length} deleted branches`}
      </summary>
      <div className="rzdel-list">
        {branches.map((branch) => (
          <div key={branch.id} className="setrow notedel" ref={ownerRef(branch.id)}>
            <span className="st">
              <b>{branch.heading}</b>
              <span>
                {branch.restores === 1
                  ? 'Deleted'
                  : `Deleted, with the ${branch.restores - 1} under it`}
              </span>
            </span>
            <button
              className="btn"
              ref={rowRef(branch.id)}
              aria-label={`Restore ${branch.heading}`}
              onClick={() => onRestore(branch)}
              disabled={busy}
            >
              Restore
            </button>
          </div>
        ))}
      </div>
    </details>
  );
}
